import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Avatar from 'components/common/Avatar';
import ErrorBoundary from 'components/common/ErrorBoundary';
import useToken from 'hooks/useToken';
import { useToast } from 'context/providers/ToastProvider';
import PropTypes from 'prop-types';
import React, { useState, useEffect } from 'react';
import { Card, Col, Form, Row, Spinner } from 'react-bootstrap';
import ComentariosSoporte from './ComentariosSoporte';
import InfiniteScroll from "react-infinite-scroll-component";
import { FiCheck } from 'react-icons/fi';
import Skeleton from 'react-loading-skeleton';

const SoporteTecnico = ({
    agrupacionLicencias = [],
    idClienteAkasia,
    emailCliente
}) => {
    const { token, setToken, profileImage } = useToken();
    const { showErrorToast } = useToast();

    const [problemaSoporte, setProblemaSoporte] = useState('');
    const [comentarios, setComentarios] = useState([]);
    const [pagina, setPagina] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const [cargando, setCargando] = useState(true);

    const HORAS_ESPERA = 3;

    // Efecto para inicializar o cambiar de cliente
    useEffect(() => {
        if (idClienteAkasia) {
            setComentarios([]);
            setPagina(1);
            setHasMore(true);
            setCargando(true);
            fetchMoreComentarios(1, true);
        }
    }, [idClienteAkasia]);

    /**
     * Función para obtener más comentarios
     */
    const fetchMoreComentarios = async (paginaActual = pagina, esCargaInicial = false) => {
        try {
            const response = await fetch("/api/cliente/ObtenerComentarios", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({
                    idClienteAkasia: idClienteAkasia,
                    pagina: paginaActual,
                    tamanioPagina: 6,
                }),
            });

            if (response.ok) {
                const data = await response.json();
                if (data.iResponseType === 1) {
                    const parsed = JSON.parse(data.detalles);
                    const nuevosComentarios = parsed.table2 || [];

                    if (nuevosComentarios.length > 0) {
                        if (esCargaInicial || paginaActual === 1) {
                            setComentarios(nuevosComentarios);
                        } else {
                            setComentarios((prev) => [...prev, ...nuevosComentarios]);
                        }

                        setPagina(paginaActual + 1);

                        if (nuevosComentarios.length < 6) {
                            setHasMore(false);
                        }
                    } else {
                        setHasMore(false);
                    }
                } else {
                    showErrorToast(data.vMessage);
                    setHasMore(false);
                }
            } else {
                showErrorToast(`${response.status} ${response.statusText}`);
                if (response.status === 401) {
                    setToken(null);
                    location.href = "";
                }
                setHasMore(false);
            }
        } catch (error) {
            showErrorToast("Error inesperado al obtener comentarios");
            setHasMore(false);
        } finally {
            setCargando(false);
        }
    };

    const crearComentarioSoporte = async () => {
        if (problemaSoporte.trim().length < 15) {
            showErrorToast('El problema debe tener al menos 15 caracteres.');
            return;
        }

        const jDataSend = {
            vProblema: problemaSoporte,
            idClienteAkasia: idClienteAkasia
        };

        try {
            const response = await fetch("/api/cliente/RegistrarProblemaSoporte/", {
                method: 'POST',
                headers: {
                    'Authorization': 'Bearer ' + token,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(jDataSend)
            });

            if (response && response.ok) {
                const data = await response.json();

                if (data.iResponseType == 1) {
                    const detalles = JSON.parse(data.detalles);
                    const comentario = detalles.table2[0];
                    setComentarios([comentario, ...comentarios]);
                    setProblemaSoporte('');
                } else {
                    showErrorToast(data.vMessage);
                }
            } else if (response) {
                showErrorToast(response.status + ' ' + response.statusText);
                if (response.status == 401) {
                    setToken(null);
                    location.href = "";
                }
            }
        } catch (error) {
            showErrorToast('Hubo un problema con la petición Fetch: ' + error);
        }
    };

    const LogSkeleton = () => (
        <div style={{ padding: 0 }}>
            {[1, 2, 3].map((i) => (
                <div key={i} style={{ display: "flex", flexDirection: "column", padding: "12px 16px", borderBottom: "1px solid #e3e3e3" }}>
                    <div className="d-flex align-items-center mb-2">
                        <Skeleton circle height={32} width={32} className="me-2" />
                        <Skeleton height={16} width="40%" />
                    </div>
                    <div style={{ paddingLeft: 40 }}>
                        <Skeleton height={14} width="90%" className="mb-1" />
                        <Skeleton height={12} width="30%" />
                    </div>
                </div>
            ))}
        </div>
    );

    if (agrupacionLicencias.length === 0) return null;

    return (
        <Row className="g-3 mb-3">
            <Col className="col-12">
                <Card className="mb-1 shadow-sm border-0">
                    <Card.Header className="border-bottom bg-light">
                        <h5 className="mb-0 fw-bold">Soporte T&eacute;cnico</h5>
                    </Card.Header>

                    <Card.Body className="p-3">
                        <Form id="comment" className="mb-3" onSubmit={(e) => { e.preventDefault(); crearComentarioSoporte(); }}>
                            <div className="d-flex align-items-start mb-2">
                                <Avatar src={profileImage} size="xl" />
                                <Form.Control
                                    type="text"
                                    className="ms-2"
                                    placeholder="Describe un nuevo problema..."
                                    value={problemaSoporte}
                                    style={{ resize: "none" }}
                                    maxLength={999}
                                    onChange={e => setProblemaSoporte(e.target.value)}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                            e.preventDefault();
                                            if (e.target.value.length >= 15) crearComentarioSoporte();
                                        }
                                    }}
                                />
                            </div>
                            {problemaSoporte.length > 0 && problemaSoporte.length < 15 && (
                                <small className="text-muted ms-5 pt-1 d-block">
                                    M&iacute;nimo 15 caracteres ({problemaSoporte.length}/15)
                                </small>
                            )}
                        </Form>

                        <hr className="text-200 my-3" />

                        <div id="scrollableTargetComentarios" style={{ maxHeight: '350px', overflow: 'auto' }}>
                            {cargando ? (
                                <LogSkeleton />
                            ) : (
                                <InfiniteScroll
                                    dataLength={comentarios.length}
                                    next={() => fetchMoreComentarios(pagina, false)}
                                    hasMore={hasMore}
                                    loader={
                                        <div className="d-flex justify-content-center align-items-center py-3">
                                            <Spinner animation="border" size="sm" variant="primary" className="me-2" />
                                            <span className="text-muted" style={{ fontSize: '0.85rem' }}>Cargando m&aacute;s registros...</span>
                                        </div>
                                    }
                                    scrollableTarget="scrollableTargetComentarios"
                                    scrollThreshold={0.95}
                                    endMessage={
                                        comentarios.length > 0 && (
                                            <div className="text-center py-3 border-top-0">
                                                <small className="text-muted" style={{ fontSize: '0.8rem' }}>
                                                    <FiCheck className="me-1" size={12} />
                                                    No hay m&aacute;s comentarios
                                                </small>
                                            </div>
                                        )
                                    }
                                >
                                    <ErrorBoundary>
                                        <ComentariosSoporte
                                            idClienteAkasia={idClienteAkasia}
                                            comentarios={comentarios}
                                            setComentarios={setComentarios}
                                            emailCliente={emailCliente}
                                        />
                                    </ErrorBoundary>
                                </InfiniteScroll>
                            )}
                        </div>
                    </Card.Body>

                    <Card.Footer className="border-top text-end bg-light">
                        <small className="text-muted">
                            <FontAwesomeIcon icon="info-circle" className="me-1" />
                            Puedes crear un ticket de soporte cada {HORAS_ESPERA} horas
                        </small>
                    </Card.Footer>
                </Card>
            </Col>
        </Row>
    );
};

SoporteTecnico.propTypes = {
    agrupacionLicencias: PropTypes.array,
    setHabilitarBtnCombinar: PropTypes.func,
    setShowModalCombinarClientes: PropTypes.func,
    idClienteAkasia: PropTypes.string,
    emailCliente: PropTypes.string
};

export default SoporteTecnico;